import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  ChevronDown,
  LogOut,
  Menu,
  Search,
  Smartphone,
  User,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { signOut } from '@/services/api/auth';
import { cn } from '@/lib/cn';
import { LogoAvatar } from '@/components/common/LogoAvatar';
import { MobileSettingsDialog } from '@/components/common/MobileSettingsDialog';
import { routes } from '@/lib/routes';
import type { Empresa } from '@/services/api/empresas';
import { NotificationBell } from './NotificationBell';
import { areSoundsEnabled, setSoundsEnabled, playSound } from '@/lib/sounds';

interface TopbarProps {
  empresaActiva: Empresa;
  onOpenSearch?: () => void;
  onOpenAudit?: () => void;
  /** TODO-MDC-21: solo mobile — abre el sidebar drawer. */
  onOpenMobileNav?: () => void;
}

export function Topbar({
  empresaActiva,
  onOpenSearch,
  onOpenAudit,
  onOpenMobileNav,
}: TopbarProps) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [soundsOn, setSoundsOn] = useState(true);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  useEffect(() => {
    setSoundsOn(areSoundsEnabled());
  }, []);

  async function onLogout() {
    await signOut();
    navigate('/login', { replace: true });
  }

  function toggleSounds() {
    const next = !soundsOn;
    setSoundsEnabled(next);
    setSoundsOn(next);
    if (next) playSound('notify');
  }

  return (
    <header className="relative z-50 flex h-14 items-center justify-between gap-2 border-b border-surface-line bg-white/70 px-3 backdrop-blur-sm md:h-16 md:px-6">
      {/* TODO-MDC-21: hamburguesa visible solo mobile, dispara global event
          que AppLayout escucha (evita prop drilling). */}
      {onOpenMobileNav && (
        <button
          type="button"
          onClick={onOpenMobileNav}
          className="grid h-9 w-9 flex-none place-items-center rounded-md text-neutral-muted hover:bg-blue-10 hover:text-blue md:hidden"
          aria-label="Abrir menú"
        >
          <Menu size={18} />
        </button>
      )}

      {/* Display informativo de la empresa activa. */}
      <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-surface-line bg-white/70 py-1 pl-1 pr-2 md:gap-2.5 md:py-1.5 md:pl-1.5 md:pr-4">
        <LogoAvatar
          src={empresaActiva.logo_url}
          name={empresaActiva.nombre}
          size={32}
          shape="square"
        />
        <div className="min-w-0 flex-1">
          <p className="hidden text-[10px] font-semibold uppercase tracking-wider text-neutral-muted md:block">
            Empresa
          </p>
          <p className="truncate text-xs font-semibold leading-tight text-neutral-ink md:text-sm">
            {empresaActiva.nombre}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {onOpenSearch && (
          <button
            type="button"
            onClick={onOpenSearch}
            title="Búsqueda global (⌘K)"
            className="flex items-center gap-2 rounded-md border border-surface-line bg-white px-2 py-1.5 text-xs text-neutral-muted transition hover:border-blue/40 hover:bg-blue-10/20 hover:text-blue md:px-3"
          >
            <Search size={14} />
            <span className="hidden md:inline">Buscar…</span>
            <kbd className="ml-4 hidden rounded border border-surface-line bg-surface-bg px-1 font-mono text-[10px] font-semibold text-neutral-ink md:inline">
              ⌘K
            </kbd>
          </button>
        )}

        <NotificationBell empresaId={empresaActiva.id} />

        {/* User menu */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 transition hover:bg-blue-10"
          >
            <div className="grid h-8 w-8 place-items-center overflow-hidden rounded-full bg-blue text-sm font-bold text-white">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="avatar"
                  className="h-full w-full object-cover"
                />
              ) : (
                initialsOf(profile?.full_name)
              )}
            </div>
            <div className="hidden text-left sm:block">
              <p className="text-sm font-semibold leading-tight text-neutral-ink">
                {profile?.full_name ?? 'Usuario'}
              </p>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-muted">
                {profile?.role}
              </p>
            </div>
            <ChevronDown size={14} className="text-neutral-muted" />
          </button>

          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-[55]"
                onClick={() => setMenuOpen(false)}
                aria-hidden
              />
              <div
                className={cn(
                  'absolute right-0 top-full z-[60] mt-2 w-56 overflow-hidden',
                  'rounded-md border border-surface-line bg-white shadow-premium',
                )}
              >
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-ink hover:bg-blue-10"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate(routes.empresa.perfil(empresaActiva.id));
                  }}
                >
                  <User size={15} />
                  <span>Mi perfil</span>
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-ink hover:bg-blue-10"
                  onClick={toggleSounds}
                >
                  {soundsOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
                  <span>Sonidos: {soundsOn ? 'activados' : 'silenciados'}</span>
                </button>
                {/* TODO-MDC-23: acceso siempre disponible al setup mobile —
                    sin esto, una vez cerrado el wizard primera vez no había
                    forma de volver a activar push o ver instrucciones de
                    instalar. */}
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-ink hover:bg-blue-10"
                  onClick={() => {
                    setMenuOpen(false);
                    setMobileSettingsOpen(true);
                  }}
                >
                  <Smartphone size={15} />
                  <span>Configuración móvil · PWA</span>
                </button>
                {onOpenAudit && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-ink hover:bg-blue-10"
                    onClick={() => {
                      setMenuOpen(false);
                      onOpenAudit();
                    }}
                  >
                    <Activity size={15} />
                    <span>Auditoría</span>
                  </button>
                )}
                <div className="border-t border-surface-line" />
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-ink hover:bg-orange/10 hover:text-orange"
                  onClick={onLogout}
                >
                  <LogOut size={15} />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {/* TODO-MDC-23: settings móvil siempre accesible (push, instalar) */}
      <MobileSettingsDialog
        open={mobileSettingsOpen}
        onClose={() => setMobileSettingsOpen(false)}
        empresaId={empresaActiva.id}
      />
    </header>
  );
}

function initialsOf(fullName: string | null | undefined): string {
  if (!fullName) return '·';
  const parts = fullName.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '·';
}
